import type {Set, List, RecordOf} from 'immutable';
import {createSelector, lruMemoize} from 'reselect';
import {getPages} from '@packages/shared/utils/PageTreeUtils';
import {isMint as isPageMint} from '@packages/systems/core/utils/RecordUtils';
import {
  countTotalCommerceItems,
  didTheUserSetUpCommerceForThisSite,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  selectCurrentLocale,
  selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale,
} from '@packages/systems/localization';
import {getTotalNumberOfItems} from '@packages/systems/dynamo/utils/CollectionUtils';
import {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import {DesignerMode} from '@packages/utilities/environment';
import {DESIGNER_SITE_MODES} from '@packages/systems/design-system/constants';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {selectActiveTutorial} from '@packages/systems/onboarding';
import type {
  DesignerEventData,
  DesignerEventName,
} from '@packages/shared/segment-typewriter';
import {getFeatureFlagOutsideOfReact} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {DESIGNER_WORKFLOWS} from '@packages/systems/designer/workflows';
import {PageState} from '@packages/systems/core/records/PageRecord';

export type AnalyticsModeType =
  | 'preview'
  | 'read only'
  | 'editor'
  | 'standalone edit'
  | 'full edit' // this is only used in the WORKSPACE_LOADED event & should probably be cleaned up at some point
  | 'read-only-mode'
  | 'designer-mode' // this is only used in the WORKSPACE_LOADED event & should probably be cleaned up at some point
  | 'anonymous-comment-mode';

export type AnalyticsStateShape = {
  analyticsReady: boolean;
  events: List<AnalyticsEvent<DesignerEventName>>;
  updatedPageSettings: Set<string>;
  sessionId: string | null;
  sessionInitiated: boolean;
  /**
   * Text nodes already reported as edited this session. CONTENT_EDITOR_TEXT_-
   * VALUE_REPLACED fires on every keystroke, so this collapses a typing burst
   * into one "Content Editor Text Edited" event per node (LIF-435).
   */
  editedTextNodeIds: Set<string>;
};

export type AnalyticsEvent<K extends DesignerEventName> = Readonly<{
  name: K;
  data?: DesignerEventData<K>;
  opts?: unknown;
  setting?: string;
  type?: string;
}>;
export type AnalyticsEventData<K extends DesignerEventName> =
  DesignerEventData<K>;

export type AnalyticsState = RecordOf<AnalyticsStateShape>;

export type StatesDependencies = {
  getFeatureFlagInsideReducer?: typeof getFeatureFlagOutsideOfReact;
  siteDataStore: DesignerStores['SiteDataStore'];
  sendFeedbackModalStore: DesignerStores['SendFeedbackModalStore'];
  ix2State: DesignerStores['IX2UiStore'];
  ix1State: DesignerStores['IXStore'];
  IX3Store?: DesignerStores['IX3Store'];
  uiState: DesignerStores['UiStore'];
  ix2UiEngineState: DesignerStores['IX2UiEngineStore'];
  designerState: DesignerStores['DesignerStore'];
  cssVariablesState: DesignerStores['CssVariablesStore'];
  commerceState: DesignerStores['CommerceStore'];
  collectionsState: DesignerStores['CollectionStore'];
  currentCollectionState: DesignerStores['CurrentCollectionStore'];
  localizationState: DesignerStores['LocalizationStore'];
  pageStore: DesignerStores['PageStore'];
  styleBlockStore: DesignerStores['StyleBlockStore'];
  uiNodeState: DesignerStores['UiNodeStore'];
  siteUsersState: DesignerStores['SiteUsersStore'];
  auditState: DesignerStores['AuditStore'];
  ftuState: DesignerStores['FTUStore'];
  accessRequestStore: DesignerStores['AccessRequestStore'];
  userStore: DesignerStores['UserStore'];
  multiContextCanvasStore: DesignerStores['MultiContextCanvasStore'];
  // Optional so existing test fixtures that build this object by hand keep
  // compiling; the reducer always passes it.
  permissionsStore?: DesignerStores['PermissionsStore'];
};

/**
 * Creates a memoized function for adding analytics events to the analytics state.
 *
 * This function is a wrapper over `addAnalyticsStoreEvent`, maintaining a curried API
 * from the legacy implementation. It uses memoization to optimize performance by
 * caching the result based on the input state and dependencies.
 *
 * @param {AnalyticsState} state - The current analytics state.
 * @param {StatesDependencies} otherStates - Object containing various store states needed for analytics.
 * @returns {Function} A memoized function that takes analytics events and returns an updated AnalyticsState.
 *
 * @example
 * const addEvents = createAnalyticsEventAdder(currentState, stateDependencies);
 * const updatedState = addEvents(event1, event2, event3);
 */
export const createAnalyticsEventAdder = lruMemoize(
  function createAnalyticsEventAdder(
    state: AnalyticsState,
    otherStates: StatesDependencies
  ): <K extends DesignerEventName>(
    ...analyticsEvents: AnalyticsEvent<K>[]
  ) => AnalyticsState {
    return function addAnalyticsEvent<K extends DesignerEventName>(
      ...analyticsEvents: AnalyticsEvent<K>[]
    ): AnalyticsState {
      return addAnalyticsStoreEvent(state, otherStates, analyticsEvents);
    };
  }
);

/**
 * Helper function for deep merging 2 dicts
 */
function deepMerge(target: any, source: any) {
  if (typeof source !== 'object' || source === null) {
    return source;
  }
  const clone = structuredClone(target);
  for (const key in source) {
    if (
      typeof source[key] === 'object' &&
      source[key] !== null &&
      key in clone
    ) {
      clone[key] = deepMerge(clone[key], source[key]);
    } else {
      clone[key] = structuredClone(source[key]);
    }
  }
  return clone;
}

/**
 * Helper function for updating analytics store events.
 *
 * This function adds one or more analytics events to the analytics state, enriching them with additional data
 * from the state dependencies. It's designed to be used by the AnalyticsStore, but can also be used
 * to create separate analytics reducers.
 *
 * @param {AnalyticsState} analyticsState - The current analytics state.
 * @param {StatesDependencies} statesDependencies - Object containing various store states needed for analytics.
 * @param {AnalyticsEvent | AnalyticsEvent[]} analyticsEvent - The event(s) to be added to the analytics state.
 *        This is the specific event data that the consumer wants to track.
 * @returns {AnalyticsState} Updated analytics state with the new event(s) added.
 */
export function addAnalyticsStoreEvent<K extends DesignerEventName>(
  analyticsState: AnalyticsState,
  statesDependencies: StatesDependencies,
  analyticsEvent: AnalyticsEvent<K> | AnalyticsEvent<K>[]
): AnalyticsState {
  const analyticsEvents = Array.isArray(analyticsEvent)
    ? analyticsEvent
    : [analyticsEvent];

  const {data: baseData, opts: baseOpts} =
    getAnalyticsContextData<K>(statesDependencies);

  return analyticsEvents.reduce((nextState, event) => {
    if (!event) return nextState;

    const data: AnalyticsEventData<K> = {...baseData, ...event.data};

    return nextState.update('events', (events) =>
      events.push({
        ...event,
        data,
        opts: event.opts ? deepMerge(baseOpts, event.opts) : baseOpts,
      })
    );
  }, analyticsState);
}

/**
 * Selects all created pages from the page store. This selector is memoized with
 * LRU caching and a size of one: it is unlikely that we will call this selector
 * with a _previous_ page store state (as it is immutable and undo/redo should
 * not have a big impact here).
 */
const selectCreatedPages = createSelector(
  (pageState: PageState) => pageState,
  (pageState) => getPages(pageState).filter((page) => !isPageMint(page)),
  {
    memoize: lruMemoize,
    memoizeOptions: {
      maxSize: 1,
    },
  }
);

/**
 * Generates additional data and options for analytics events based on the current state of the application.
 *
 * This function aggregates various pieces of information from different parts of the application state,
 * including site data, designer state, commerce state, page information, and more. It's used to enrich
 * analytics events with contextual data.
 *
 * @param {StatesDependencies} statesDependencies - An object containing various store states needed for analytics.
 * @returns {{data: AnalyticsEvent['data'], opts: Record<string, unknown>}} An object containing:
 *   - data: Additional data to be included with each analytics event.
 *   - opts: Options for analytics integrations (currently empty, but prepared for future use).
 *
 * @example
 * const {data, opts} = getAnalyticsContextData(statesDependencies);
 * // Use data and opts to enrich analytics events
 */
function getAnalyticsContextData<K extends DesignerEventName>(
  statesDependencies: StatesDependencies
): {
  data: AnalyticsEventData<K>;
  opts: Record<string, unknown>;
} {
  const {
    siteDataStore,
    designerState,
    commerceState,
    pageStore,
    styleBlockStore,
    ftuState,
    collectionsState,
    localizationState,
    currentCollectionState,
    uiNodeState,
    multiContextCanvasStore,
    permissionsStore,
  } = statesDependencies;

  const createdPages = selectCreatedPages(pageStore);

  const siteSubscriptionPlan =
    siteDataStore &&
    siteDataStore.siteSubscription &&
    siteDataStore.siteSubscription.type
      ? siteDataStore.siteSubscription.type
          .replace('_', ' ')
          .replace('static', 'basic')
      : 'free';

  const ecommItemCount = collectionsState
    ? countTotalCommerceItems(
        collectionsState.collections,
        selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale({
          CollectionStore: collectionsState,
          CurrentCollectionStore: currentCollectionState,
          LocalizationStore: localizationState,
        })
      )
    : 0;

  const mode = getEnvironmentMode(designerState);
  const totalNumberOfItems = collectionsState
    ? getTotalNumberOfItems(
        selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale({
          CollectionStore: collectionsState,
          CurrentCollectionStore: currentCollectionState,
          LocalizationStore: localizationState,
        })
      )
    : 0;

  const siteRole = siteDataStore && siteDataStore.role;

  const componentPages = pageStore.get('componentPages');
  const currentComponentPage =
    componentPages && componentPages.get(designerState.currentPageId);

  const isOnComponentPage = Boolean(currentComponentPage);

  const isMultiContextCanvasActive =
    multiContextCanvasStore.isEditingPageOnOpenCanvas;

  const componentCanvasComponentId = isOnComponentPage
    ? currentComponentPage?.pageKind?.meta.componentId
    : undefined;

  const pageId = designerState.currentPageId || null;
  const canvasPageId = isOnComponentPage ? null : pageId;
  const isFocusedInComponent = Boolean(
    isOnComponentPage || uiNodeState.focusedSymbolNativeIdPath.length
  );

  let canvasType = 'page';
  if (isOnComponentPage) {
    canvasType = 'symbol';
  } else if (designerState.workflow === DESIGNER_WORKFLOWS.CANVAS) {
    canvasType = isMultiContextCanvasActive ? 'open' : 'fixed';
  }

  const data: Record<string, any> = {
    // For now there is only one active tutorial. If multiple tutorials are available, comma delimit
    'active tutorials': selectActiveTutorial(ftuState) || '',
    // WXP IA
    'wxp enabled': true,
    // WXP IA Version
    'wxp version': 2,
    // Designer Workflow
    'designer workflow': designerState.workflow,
    // Site properties
    'plan type': siteSubscriptionPlan,
    'site role': siteRole,
    // The role the session is *impersonating*, which is how AISB enters Build
    // Mode (useFinalizeOnboarding dispatches simulateRoleStarted with
    // page_builder_v2). `site role` above is the user's real assigned role, so
    // it reads owner/admin for an AISB site creator and cannot identify a Build
    // Mode session on its own. Reported raw rather than as a derived
    // "in build mode" boolean so it can't drift from the isInPageBuildMode
    // ability, which is evaluated against full designer state we don't have here.
    'simulated site role': permissionsStore?.simulatedSiteRole ?? null,
    // Whether this site was created by AI Site Builder. `simulated site role`
    // above identifies a Build Mode session but NOT an AISB one — the File menu
    // role picker and the ?simulateRole param produce the same value on any
    // site — so isolating the AISB cohort otherwise needs a site-dimension join
    // in every query. Read from the persisted site field, not a feature flag,
    // so it stays correct after the AISB gates are cleaned up (LIF-436).
    'is aisb site': Boolean(siteDataStore.isAISiteGen),
    // Which AISB variant the session is running. `is aisb site` above says the
    // site came from AISB at all; this says whether v2 was on while the user
    // worked in it, which is the TRUE-vs-FALSE comparison the launch is judged
    // on. It cannot be recovered downstream: Statsig gate exposures are not
    // forwarded to Amplitude, so no gate dimension exists there. Unlike
    // `is aisb site` this is deliberately a live flag read, because the
    // question is what the user experienced in this session.
    'aisb v2 enabled': getFeatureFlagOutsideOfReact(FEATURE_FLAGS.AISB_V2),
    // Ecommerce
    'ecommerce enabled': commerceState
      ? didTheUserSetUpCommerceForThisSite(commerceState) || ecommItemCount > 0
      : false,
    // CMS
    'total collection count': collectionsState
      ? collectionsState.collections.size
      : null,
    'ecommerce item count': ecommItemCount,
    'project item count': collectionsState ? totalNumberOfItems : null,
    'cms item count': collectionsState
      ? totalNumberOfItems - ecommItemCount
      : null,
    // TODO:  add these properties
    // 'is paid': '',
    'styles count': styleBlockStore && styleBlockStore.styleBlocks.size,
    'site published': Boolean(
      siteDataStore.lastPublished || siteDataStore.lastPublicationId
    ),
    'page count': createdPages.size,
    'canvas type': canvasType,
    'canvas symbol id': componentCanvasComponentId ?? null,
    'canvas page id': canvasPageId,
    // These props are used in Snowflake for session data aggregation
    'page id': pageId,
    'site id': siteDataStore.siteId || null,
    tutorial: siteDataStore.tutorial,
    mode,
    'focused in component': isFocusedInComponent,
  };

  // Add group Amplitude integration
  const groups: Record<string, any> = {};
  if (siteDataStore.siteId) {
    groups.site = siteDataStore.siteId;
  }

  // Workspace properties
  const {
    workspaceId,
    workspacePlan,
    workspaceSeatCount,
    workspaceIsEnterprise,
  } = siteDataStore;
  if (workspaceId) {
    Object.assign(data, {
      'workspace id': workspaceId,
      'workspace plan': workspacePlan,
      'workspace seat count': workspaceSeatCount,
      'workspace is enterprise': workspaceIsEnterprise,
    });
  }

  // Legacy team properties
  if (siteDataStore.isOrg) {
    groups.team = siteDataStore.orgId;
    data['project count'] = siteDataStore.orgTotalSites;
    data['legacy seat count'] = siteDataStore.orgSeats;
    data['legacy team id'] = siteDataStore.orgId;
    data['enterprise billing'] = siteDataStore.isOrgEnterprise;
  }

  // todo if we ever enable this, make sure that we deep merge these default opts with events having
  // an explicitly defined `opts` property.
  // See the result.update() call in the return statement of this closure.
  const opts: Record<string, any> = {
    context: {
      webflow_page_id: designerState.currentPageId || null,
      webflow_site_id: siteDataStore.siteId || null,
      webflow_locale_id: selectCurrentLocale(localizationState)?._id ?? null,
    },
  };

  return {
    data: data as AnalyticsEventData<K>,
    opts,
  };
}

/**
 * Helper function to determine the current environment mode
 * ex. preview, read only, editor, standalone edit, full edit
 * for analytics purposes.
 */
function getEnvironmentMode({siteMode}: DesignerStateType): AnalyticsModeType {
  // PREVIEW needs to be before READ_ONLY since READ_ONLY is preview || p
  if (DesignerMode.PREVIEW) {
    return 'preview';
  }

  if (DesignerMode.READ_ONLY) {
    return 'read only';
  }

  if (DesignerMode.ANONYMOUS_COMMENT) {
    return 'anonymous-comment-mode';
  }

  if (siteMode === DESIGNER_SITE_MODES.EDIT) {
    return 'standalone edit';
  }

  return 'full edit';
}
